/**
 * La franja de lo urgente, encima de todo · 13-10-2026
 *
 * Solo aparece si hay algo urgente (`high`): el contrato sin firmar para el dueño,
 * una clase de prueba sin contestar, el panel en solo lectura… Lo menos urgente se
 * queda en la campana, porque una franja que está siempre encendida enseña a no
 * leerla.
 *
 * Se puede cerrar, y se queda cerrada **hasta que cambie lo urgente**: si mañana
 * aparece otra cosa, vuelve. Se recuerda en la pestaña (`sessionStorage`), no para
 * siempre.
 */
import { useState } from 'react';
import { Icono } from '@/shared/ui/Icono';
import { urgentes, type Pendiente } from '@/shared/ui/pendientes/tipos';

const CLAVE = 'kiap.urgentes-vistos';

function leer(): string | null {
  try {
    return sessionStorage.getItem(CLAVE);
  } catch {
    return null;
  }
}

export function FranjaDeUrgentes({
  pendientes,
  alIr,
  todas,
}: {
  pendientes: readonly Pendiente[] | undefined;
  alIr: (destino: string) => void;
  /** La pantalla con la lista entera —Inicio, o Hoy en el back office—. */
  todas: string;
}) {
  const lista = urgentes(pendientes ?? []);
  const firma = lista.map((p) => p.clave).join(',');
  const [cerrada, setCerrada] = useState<string | null>(leer);

  if (lista.length === 0 || cerrada === firma) return null;

  const primera = lista[0]!;
  const una = lista.length === 1;
  // Una sola, a resolverla; varias, a la lista, donde cada una tiene su botón.
  const destino = una ? primera.destino : todas;
  const texto = (
    <>
      <strong>
        {una
          ? primera.titulo
          : `${lista.length} cosas urgentes: ${primera.titulo.charAt(0).toLowerCase()}${primera.titulo.slice(1)} y ${lista.length - 1} más`}
      </strong>
      {destino && (
        <span className="franja-urgente-ir">
          {una ? primera.accion : 'Verlas'}
          <Icono nombre="flecha" tamano={16} />
        </span>
      )}
    </>
  );

  return (
    <div className="franja-urgente" role="alert">
      <Icono nombre="alerta" tamano={20} />
      {destino ? (
        <button
          type="button"
          className="franja-urgente-texto"
          onClick={() => alIr(destino)}
        >
          {texto}
        </button>
      ) : (
        <div className="franja-urgente-texto">{texto}</div>
      )}
      <button
        type="button"
        className="franja-urgente-cerrar"
        aria-label="Ocultar hasta que haya algo nuevo"
        title="Ocultar hasta que haya algo nuevo"
        onClick={() => {
          try {
            sessionStorage.setItem(CLAVE, firma);
          } catch {
            /* sin almacenamiento, se oculta igual mientras dure la página */
          }
          setCerrada(firma);
        }}
      >
        <Icono nombre="cerrar" tamano={16} />
      </button>
    </div>
  );
}
